import { TwoLetterISOLanguage } from '@dustin-web/microsite-config'
import Image from '@dustin-web/microsite-components/src/image'
import {
  PaymentMethod,
  PaymentMethodsMetadata,
} from '@dustin-web/microsite-config/src/site-context'

type PaymentMethodImageData = {
  type: PaymentMethod
  src: string
  alt: string
  width: number
  height: number
}

type PaymentMethodSrc = {
  light: string | Record<TwoLetterISOLanguage, string>
  dark: string | Record<TwoLetterISOLanguage, string>
}

const paymentMethodsSrc: Record<PaymentMethod, PaymentMethodSrc> = {
  [PaymentMethod.KLARNA]: {
    light: 'https://media.dustin.eu/content/88621/klarna.svg',
    dark: 'https://media.dustin.eu/content/88621/klarna.svg',
  },
  [PaymentMethod.SWISH]: {
    light: 'https://media.dustin.eu/content/88625/swish.svg',
    dark: 'https://media.dustin.eu/content/88625/swish.svg',
  },
  [PaymentMethod.MASTERCARD]: {
    light: 'https://media.dustin.eu/content/5zlplmve/mastercard.svg',
    dark: 'https://media.dustin.eu/content/5zlplmve/mastercard.svg',
  },
  [PaymentMethod.VISA]: {
    light: 'https://media.dustin.eu/content/wk4g3q33/visa-blue.svg',
    dark: 'https://media.dustin.eu/content/1onlypxr/visa-white.svg',
  },
  [PaymentMethod.VISADK]: {
    light: 'https://media.dustin.eu/content/88628/visadankort.svg',
    dark: 'https://media.dustin.eu/content/88628/visadankort.svg',
  },
  [PaymentMethod.TRUSTLY]: {
    light: 'https://media.dustin.eu/content/gjgjoyt3/trustly-black.svg',
    dark: 'https://media.dustin.eu/content/jfujnjux/trustly-white.svg',
  },
  [PaymentMethod.IDEAL]: {
    light: 'https://media.dustin.eu/content/88629/ideal.svg',
    dark: 'https://media.dustin.eu/content/88629/ideal.svg',
  },
  [PaymentMethod.PAYTRAIL]: {
    light: 'https://media.dustin.eu/content/88630/paytrail.svg',
    dark: 'https://media.dustin.eu/content/88630/paytrail.svg',
  },
  [PaymentMethod.MOBILEPLAY]: {
    light: 'https://media.dustin.eu/content/znppu0sb/mobilepay.svg',
    dark: 'https://media.dustin.eu/content/znppu0sb/mobilepay.svg',
  },
  [PaymentMethod.INVOICE]: {
    light: {
      en: 'https://media.dustin.eu/content/ra1hzqjc/invoice-black-en.svg',
      fr: 'https://media.dustin.eu/content/ra1hzqjc/invoice-black-en.svg',
      sv: 'https://media.dustin.eu/content/qxvcbgks/invoice-black-se.svg',
      fi: 'https://media.dustin.eu/content/kssdqont/invoice-black-fi.svg',
      nb: 'https://media.dustin.eu/content/vyzfvznv/invoice-black-no.svg',
      da: 'https://media.dustin.eu/content/u0lhxghg/invoice-black-dk.svg',
      nl: 'https://media.dustin.eu/content/gzmoavec/invoice-black-nl.svg',
    },
    dark: {
      en: 'https://media.dustin.eu/content/jrpjrjib/invoice-white-en.svg',
      fr: 'https://media.dustin.eu/content/jrpjrjib/invoice-white-en.svg',
      sv: 'https://media.dustin.eu/content/x21ls52d/invoice-white-se.svg',
      fi: 'https://media.dustin.eu/content/gd2hhu03/invoice-white-fi.svg',
      nb: 'https://media.dustin.eu/content/4q2d3faa/invoice-white-no.svg',
      da: 'https://media.dustin.eu/content/fzdd31ga/invoice-shite-dk.svg',
      nl: 'https://media.dustin.eu/content/vigdrcnk/invoice-white-nl.svg',
    },
  },
} as const

type Props = {
  className?: string
  paymentMethods: { type: PaymentMethod; width: number; height: number }[]
  language?: TwoLetterISOLanguage
  onBackground?: 'light' | 'dark'
}

export const PaymentMethodsImages = ({
  className,
  paymentMethods,
  language,
  onBackground = 'light',
}: Props) => {
  const themedPaymentMethodsImages = Object.fromEntries(
    Object.entries(paymentMethodsSrc).map(([key, value]) => [
      key,
      onBackground === 'light' ? value.light : value.dark,
    ])
  )

  const imagesProps: PaymentMethodImageData[] = paymentMethods
    .map(method => {
      let imageSrc = themedPaymentMethodsImages[method.type]

      if (!imageSrc) {
        return
      }

      if (language && typeof imageSrc === 'object') {
        imageSrc = imageSrc[language]
      }

      return {
        ...method,
        alt: PaymentMethodsMetadata[method.type]?.name ?? `${method.type}`,
        src: imageSrc as string,
      }
    })
    .filter((value): value is PaymentMethodImageData => value !== undefined)
  return imagesProps.map((props, index) => (
    <Image priority key={index} className={className} {...props} />
  ))
}
